/**
 * Refer to https://github.com/enzymejs/enzyme/issues/2176 for how to test React Contexts with a shallow
 * enzyme renderer
 */
import type { ReactNode } from "react";

import { createContext, useReducer, use } from "react";
import { SpanHighlightObject } from "../../models/HighlightObject";

const initialState: State = {
  pageIndex: -1,
  docUuid: "",
  boxes: [],
  enabled: false,
};
type SpanHighlightProviderProps = {
  children: ReactNode;
};
type State = {
  /** UNUSED: Page index to render on. Can be -1 */
  pageIndex: number;
  /** Span Highlights to render */
  boxes: SpanHighlightObject[];
  /**  */
  enabled: boolean;
  /** UNUSED: Document UUID. Can be empty */
  docUuid: string;
};
type Action = {
  type: "set";
  docUuid: string;
  pageIndex: number;
  highlight: SpanHighlightObject | SpanHighlightObject[];
  enabled: boolean;
};
type Dispatch = (action: Action) => void;

const SpanHighlightStateContext = createContext<State | undefined>(undefined);
SpanHighlightStateContext.displayName = "SpanHighlightStateContext";
const SpanHighlightDispatchContext = createContext<Dispatch | undefined>(
  undefined,
);
SpanHighlightDispatchContext.displayName = "SpanHighlightDispatchContext";
// TODO: DRY
function highlightReducer(state: State, action: Action): State {
  const reducingHighlights = Array.isArray(action.highlight)
    ? action.highlight
    : [action.highlight];
  switch (action.type) {
    // remove and add combined
    case "set": {
      return {
        pageIndex: action.pageIndex,
        docUuid: action.docUuid,
        boxes: reducingHighlights,
        enabled: action.enabled,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}
const SpanHighlightProvider = (props: SpanHighlightProviderProps) => {
  const [state, dispatch] = useReducer(highlightReducer, initialState);

  return (
    <SpanHighlightStateContext value={state}>
      <SpanHighlightDispatchContext value={dispatch}>
        {props.children}
      </SpanHighlightDispatchContext>
    </SpanHighlightStateContext>
  );
};
function useSpanHighlightState() {
  const context = use(SpanHighlightStateContext);
  if (context === undefined) {
    throw new Error(
      "useHighlightState must be used within a SpanHighlightProvider",
    );
  }
  return context;
}
function useSpanHighlightDispatch() {
  const context = use(SpanHighlightDispatchContext);
  if (context === undefined) {
    throw new Error(
      "useHighlightDispatch must be used within a SpanHighlightProvider",
    );
  }
  return context;
}
/**
 * When needing to access the Highlight Provider from within a Class based component, use this
 */
function SpanHighlightConsumer({ children }: any) {
  return (
    <SpanHighlightStateContext.Consumer>
      {(context) => {
        if (context === undefined) {
          throw new Error(
            "SpanHighlightConsumer must be used within a SpanHighlightProvider",
          );
        }
        return children(context);
      }}
    </SpanHighlightStateContext.Consumer>
  );
}
/**
 * When needing to dispatch to Highlight Provider from a Class based component, use this
 */
function SpanHighlightDispatcher({ children }: any) {
  return (
    <SpanHighlightDispatchContext.Consumer>
      {(context) => {
        if (context === undefined) {
          throw new Error(
            "SpanHighlightDispatcher must be used within a SpanHighlightProvider",
          );
        }
        return children(context);
      }}
    </SpanHighlightDispatchContext.Consumer>
  );
}
export {
  SpanHighlightProvider,
  useSpanHighlightState,
  useSpanHighlightDispatch,
  SpanHighlightConsumer,
  SpanHighlightDispatcher,
};
export { State as SpanHighlightProviderState };
/**
 * Exported so RemoteImageViewer can access and update state
 */
export {
  SpanHighlightStateContext,
  SpanHighlightDispatchContext,
  Dispatch as SpanHighlightProviderDispatch,
};
